import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  legacyThousands,
  legacyPendingArrivedAt,
  LegacyTimeCountdown,
  type LegacyOrderDetail,
} from '@/entities/legacy-order';
import { extraWaitPrice, priceBreakdown } from '../lib/order-detail';
import { LegacyInfoBox } from './legacy-info-box';

/** How often the accruing wait charge is recomputed while a courier waits. */
const WAIT_TICK_MS = 30_000;

/**
 * The two price cards, reproducing the old panel's `PriceFormula` — including
 * its chrome: each card is a 20px-radius white box WRAPPED in an outer
 * 24px-radius 1px-grey border, and the pair stays side by side at every width
 * (the source's row never stacks).
 *
 * While a courier is waiting at a pickup (stage 8, scooter tariff), the charge
 * past the free window accrues in real time, so the figure ticks up on screen,
 * and the wait line's TITLE carries the live waiting timer (`PriceFormula.tsx:
 * 107-118` — the timer shows at stage 8 regardless of tariff; only the CHARGE
 * is scooter-only). The arithmetic lives in `lib/order-detail`; this component
 * owns only the clock.
 *
 * The source's own asymmetry is preserved: the accruing amount is added to the
 * TOTAL and to the wait line, but the delivery-price line subtracts only the
 * stored `wait_price`. See `priceBreakdown`.
 */
export function LegacyPriceFormula({ order }: { order: LegacyOrderDetail }) {
  const { t } = useTranslation('legacy');
  // Only ticks while there is something to accrue — a finished order sets up
  // no interval at all.
  const accruing = order.stage === 8 && order.delivery.equipment_id === 1;

  // A clock tick, not the amount. Deriving the charge from it (rather than
  // storing the number) keeps the effect free of setState: `extraWaitPrice` is
  // pure, so re-reading it each render is what makes the figure advance.
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!accruing) return;
    const id = setInterval(() => setTick((n) => n + 1), WAIT_TICK_MS);
    return () => clearInterval(id);
  }, [accruing]);

  const extra = accruing ? extraWaitPrice(order) : 0;
  const price = priceBreakdown(order, extra);
  const sum = (n: number) => `${legacyThousands(n)} ${t('uzs')}`;

  return (
    <div className="flex w-full gap-5">
      <Card>
        <LegacyInfoBox title={t('single.total_price')} info={sum(price.total)} />
        <LegacyInfoBox
          title={
            <span className="flex items-center gap-2">
              {t('single.wait_price')}
              {/* The live waiting timer, counting up from the courier's
                  arrival at the pending pickup. `dayjs(undefined)` in the
                  source meant "now" — hence the fallback. */}
              {order.stage === 8 && (
                <LegacyTimeCountdown
                  endTime={legacyPendingArrivedAt(order.origin) ?? new Date()}
                />
              )}
            </span>
          }
          info={sum(price.wait)}
        />
        <LegacyInfoBox
          title={t('single.is_product_paid')}
          info={order.delivery.product_paid ? t('single.paid') : t('single.notpaid')}
        />
      </Card>

      <Card>
        <LegacyInfoBox title={t('single.original_price')} info={sum(price.original)} />
        <LegacyInfoBox title={t('single.products_price')} info={sum(price.products)} />
        {/* Discount is a PERCENTAGE here, not a sum. */}
        <LegacyInfoBox
          title={t('single.discount')}
          info={`${legacyThousands(price.discount)} %`}
        />
      </Card>
    </div>
  );
}

/**
 * One price card: the source wraps the 20px-radius inner box in an OUTER
 * bordered 24px-radius box (`PriceFormula.tsx:129-150`).
 */
function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex w-full items-center rounded-[24px] border border-[var(--lg-grey-200)] bg-[var(--lg-surface)]">
      <div className="flex min-h-[206px] w-full flex-col gap-5 rounded-[20px] bg-[var(--lg-surface)] p-5">
        {children}
      </div>
    </div>
  );
}
